import { useState, useMemo, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, CheckCircle2, AlertCircle } from 'lucide-react'
import { masterclassService } from '../../services/masterclassService'
import {
  getTrackingContext,
  trackMasterclassRegStarted,
  trackMasterclassRegCompleted,
} from '../../services/websiteTracker'
import LoadingSpinner from '../LoadingSpinner'

const INTERESTS = [
  'Photography', 'Videography', 'Graphic Design', 'Motion Graphics',
  'Web Design', 'Digital Marketing', 'AI', 'Film & TV', 'Music Production',
  'Content Creation', 'Entrepreneurship', 'Other',
]
const GENDERS = ['Male', 'Female', 'Prefer not to say']

const GENDER_OPTIONS = GENDERS.map(g => ({ value: g, label: g }))
const INTEREST_OPTIONS = INTERESTS.map(c => ({ value: c, label: c }))

const REQUIRED_FIELDS = [
  'first_name', 'last_name', 'email', 'phone', 'gender', 'age',
  'current_course',
]

const initialForm = {
  first_name: '', last_name: '', email: '', phone: '',
  gender: '', age: '', current_course: '',
  questions: '', agree_reminders: false,
}

const FIELD_BORDER =
  'border-white/10 hover:border-cinema-glow focus:border-cinema-glow focus:shadow-[0_0_0_4px_rgba(238,141,33,0.12)]'

function FieldLabel({ children, required, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block font-sans text-sm text-white/80 mb-2"
    >
      {children}
      {required && <span className="text-cinema-glow ml-0.5" aria-hidden="true">*</span>}
    </label>
  )
}

function FieldError({ message }) {
  if (!message) return null
  return (
    <p className="text-red-400 text-xs font-sans mt-1.5 flex items-center gap-1.5">
      <AlertCircle className="w-3 h-3 shrink-0" />
      {message}
    </p>
  )
}

function CustomSelect({ id, value, onChange, options, placeholder, error }) {
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const containerRef = useRef(null)
  const buttonRef = useRef(null)
  const listRef = useRef(null)

  const selected = options.find(o => o.value === value)

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (open) {
      const idx = options.findIndex(o => o.value === value)
      setHighlighted(idx >= 0 ? idx : 0)
    }
  }, [open, options, value])

  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[data-highlighted="true"]')
      active?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlighted, open])

  const handleKeyDown = (e) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        buttonRef.current?.focus()
        break
      case 'ArrowDown':
        e.preventDefault()
        setHighlighted(i => (i + 1) % options.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlighted(i => (i - 1 + options.length) % options.length)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        onChange(options[highlighted].value)
        setOpen(false)
        buttonRef.current?.focus()
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  const selectValue = (next) => {
    onChange(next)
    setOpen(false)
    buttonRef.current?.focus()
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        ref={buttonRef}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleKeyDown}
        className={`w-full h-12 bg-white/[0.03] border px-4 text-left flex items-center justify-between
          transition-all duration-[250ms] ease focus:outline-none
          ${error
            ? 'border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.12)]'
            : FIELD_BORDER
          }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={error ? 'true' : 'false'}
      >
        <span className={selected ? 'text-white font-sans text-sm' : 'text-white/35 font-sans text-sm'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-white/35 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-cinema-graphite border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.5)] max-h-60 overflow-auto outline-none"
          role="listbox"
          aria-labelledby={id}
          tabIndex={-1}
        >
          {options.map((option, idx) => {
            const isSelected = value === option.value
            const isHighlighted = highlighted === idx
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                data-highlighted={isHighlighted}
                onClick={() => selectValue(option.value)}
                onMouseEnter={() => setHighlighted(idx)}
                className={`px-4 py-3 text-sm font-sans cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-cinema-glow/20 text-white'
                    : isHighlighted
                    ? 'bg-white/10 text-white'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                {option.label}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default function MasterclassRegistrationForm({ masterclass, onSuccess }) {
  const [form, setForm] = useState(initialForm)
  const [touched, setTouched] = useState({})
  const [attempted, setAttempted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }))
    setTouched(t => ({ ...t, [k]: true }))
  }

  const errors = useMemo(() => {
    const next = {}
    REQUIRED_FIELDS.forEach(field => {
      const value = form[field]
      if (!value || (typeof value === 'string' && !value.trim())) {
        next[field] = 'This field is required'
      }
    })
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'Please enter a valid email address'
    }
    if (form.age && (Number(form.age) < 14 || Number(form.age) > 70)) {
      next.age = 'Age must be between 14 and 70'
    }
    if (!form.agree_reminders) {
      next.agree_reminders = 'Please agree to receive event reminders'
    }
    return next
  }, [form])

  const isValid = Object.keys(errors).length === 0
  const showError = (field) => (attempted || touched[field]) && errors[field]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setAttempted(true)
    if (!isValid) {
      const firstInvalid = document.querySelector('[aria-invalid="true"]')
      firstInvalid?.focus()
      return
    }
    setError('')
    setLoading(true)
    try {
      const tracking = getTrackingContext()
      const data = await masterclassService.register({
        ...form,
        masterclass_id: masterclass?.id,
        is_current_student: false,
        level: null,
        visitor_id: tracking.visitorId,
        session_id: tracking.sessionId,
        utm_source: tracking.utmSource,
        utm_medium: tracking.utmMedium,
        utm_campaign: tracking.utmCampaign,
        utm_term: tracking.utmTerm,
        utm_content: tracking.utmContent,
        landing_page: typeof window !== 'undefined' ? window.location.pathname : null,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
      })
      trackMasterclassRegCompleted(masterclass?.id, data?.registration?.registration_number)
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        onSuccess(data.registration)
      }, 700)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const inputBase = (field) => [
    'w-full h-12 bg-white/[0.03] border px-4 text-white placeholder-white/35',
    'transition-all duration-[250ms] ease',
    'focus:outline-none',
    showError(field)
      ? 'border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.12)]'
      : FIELD_BORDER,
  ].join(' ')

  const inputProps = (field, type = 'text') => ({
    id: field,
    name: field,
    type,
    value: form[field],
    onChange: e => set(field, e.target.value),
    'aria-invalid': showError(field) ? 'true' : 'false',
    'aria-describedby': showError(field) ? `${field}-error` : undefined,
    className: inputBase(field),
  })

  return (
    <div className="w-full max-w-2xl mx-auto">
      <p className="mb-8 text-sm text-cinema-soft font-sans leading-relaxed">
        <span className="text-white">Guests: UGX 150,000.</span> Register below, then complete payment from your event pass.{' '}
        <span className="text-white">SMA students attend free</span> and can register from the{' '}
        <Link to="/student/library" className="text-cinema-glow underline underline-offset-4 hover:text-cinema-amber">
          Student Portal
        </Link>
        .
      </p>

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {error && (
          <div
            className="flex items-center gap-3 border border-red-500/30 bg-red-950/30 p-4 text-red-400 text-sm font-sans"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel htmlFor="first_name" required>First name</FieldLabel>
            <input {...inputProps('first_name')} autoComplete="given-name" required />
            <FieldError message={showError('first_name') ? errors.first_name : ''} />
          </div>
          <div>
            <FieldLabel htmlFor="last_name" required>Last name</FieldLabel>
            <input {...inputProps('last_name')} autoComplete="family-name" required />
            <FieldError message={showError('last_name') ? errors.last_name : ''} />
          </div>
          <div>
            <FieldLabel htmlFor="email" required>Email</FieldLabel>
            <input {...inputProps('email', 'email')} autoComplete="email" required />
            <FieldError message={showError('email') ? errors.email : ''} />
          </div>
          <div>
            <FieldLabel htmlFor="phone" required>Phone</FieldLabel>
            <input {...inputProps('phone', 'tel')} placeholder="+256 7XX XXX XXX" autoComplete="tel" required />
            <FieldError message={showError('phone') ? errors.phone : ''} />
          </div>
          <div>
            <FieldLabel htmlFor="gender" required>Gender</FieldLabel>
            <CustomSelect
              id="gender"
              value={form.gender}
              onChange={v => set('gender', v)}
              options={GENDER_OPTIONS}
              placeholder="Select"
              error={showError('gender')}
            />
            <FieldError message={showError('gender') ? errors.gender : ''} />
          </div>
          <div>
            <FieldLabel htmlFor="age" required>Age</FieldLabel>
            <input {...inputProps('age', 'number')} min={14} max={70} required />
            <FieldError message={showError('age') ? errors.age : ''} />
          </div>
        </div>

        <div>
          <FieldLabel htmlFor="current_course" required>Area of interest</FieldLabel>
          <CustomSelect
            id="current_course"
            value={form.current_course}
            onChange={v => set('current_course', v)}
            options={INTEREST_OPTIONS}
            placeholder="Select"
            error={showError('current_course')}
          />
          <FieldError message={showError('current_course') ? errors.current_course : ''} />
        </div>

        <div>
          <FieldLabel htmlFor="questions">A question for the speaker (optional)</FieldLabel>
          <textarea
            id="questions"
            name="questions"
            value={form.questions}
            onChange={e => set('questions', e.target.value)}
            rows={3}
            className={`${inputBase('questions')} !h-auto min-h-[96px] py-3 resize-none`}
            aria-invalid="false"
          />
        </div>

        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              id="agree_reminders"
              name="agree_reminders"
              checked={form.agree_reminders}
              onChange={e => set('agree_reminders', e.target.checked)}
              aria-invalid={showError('agree_reminders') ? 'true' : 'false'}
              aria-describedby={showError('agree_reminders') ? 'agree_reminders-error' : undefined}
              className="mt-1 h-4 w-4 shrink-0 accent-[#EE8D21]"
            />
            <span className="text-sm text-white/75 font-sans leading-relaxed">
              Send me event reminders and updates from Synergy Multimedia Academy.
            </span>
          </label>
          <div id="agree_reminders-error">
            <FieldError message={showError('agree_reminders') ? errors.agree_reminders : ''} />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || success}
          className="cinema-btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
          aria-live="polite"
        >
          {success ? (
            <>
              <CheckCircle2 className="w-5 h-5" />
              Registered!
            </>
          ) : loading ? (
            <>
              <LoadingSpinner size="sm" inline />
              Registering…
            </>
          ) : (
            'Register'
          )}
        </button>

        <p className="text-center text-xs text-cinema-muted font-sans">
          Limited seats · Doors close 15 minutes before the start · Your details are never shared.
        </p>
      </form>
    </div>
  )
}
